/**
 * RWBR — GETTING THE PAPER. D-EZ-50, the browser half.
 *
 * One function a screen calls, and it always returns an article. What it does
 * not do is fail: there is no state of this application in which the modal opens
 * onto an error, because `fallbackPaper` can write the whole thing out of the
 * same facts with no network at all.
 *
 *     cached?            → hand it back, no call, no charge
 *     a key somewhere?   → ask the Worker, cache what comes back
 *     anything went wrong → write it here
 *
 * ===========================================================================
 * WHY THE CACHE IS ON DISK AND WHY ONLY HALF OF IT IS
 * ===========================================================================
 *
 * An edition is written once. Re-opening race 14's paper a month later must not
 * buy a second completion — it is the same race, the same facts and the same
 * article, and a player who scrolls back through a season would otherwise pay
 * for every week twice.
 *
 * ⚠ ONLY THE MODEL'S ARTICLES ARE PERSISTED. A fallback article is a pure
 * function of facts that are already on hand, so storing one buys nothing — and
 * it would do real harm: a player who reads race 3 with no key, then locks a key
 * in, would still be shown the written-here version of race 3 for ever. Cache
 * what was expensive; recompute what was free.
 */

import type { PaperArticle } from '../domain/paper/fallbackPaper.ts';
import { fallbackPaper } from '../domain/paper/fallbackPaper.ts';
import type { PaperFacts } from '../domain/paper/paperFacts.ts';
import { angleFor } from '../domain/paper/angle.ts';
import { aiAccess, aiSetupVersion, isNoModel } from './aiKey.ts';

const STORAGE_KEY = 'rwbr.paper.v1';
/**
 * How many editions are kept. Thirty-six races plus a season edition is one
 * year; sixty is the current season and most of the one before it, which is as
 * far back as anybody scrolls. Oldest out first.
 */
const CACHE_LIMIT = 60;

/**
 * A reasoning model at high effort is not fast, and this call is made when the
 * race is already over and nobody is waiting on a turn of the tape. Long enough
 * to let it finish; short enough that a hung request does not leave a modal
 * spinning while a player wonders whether to reload the page.
 */
const TIMEOUT_MS = 60_000;

export type { PaperArticle };

/** The key an edition is filed under. Stable across sittings and reloads. */
export function editionKey(facts: PaperFacts): string {
  return [facts.league, `s${facts.season}`, facts.edition, `r${facts.racesRun}`].join('|');
}

// ---------------------------------------------------------------------------
// The cache
// ---------------------------------------------------------------------------

type Cache = Record<string, PaperArticle>;

function readCache(): Cache {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Cache;
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(cache: Cache): void {
  try {
    const keys = Object.keys(cache);
    // Insertion order is what `Object.keys` gives back for string keys, which is
    // the order editions were written — so dropping from the front drops the
    // oldest, and no timestamp has to be stored to know which that is.
    const trimmed =
      keys.length <= CACHE_LIMIT
        ? cache
        : Object.fromEntries(keys.slice(keys.length - CACHE_LIMIT).map((k) => [k, cache[k]]));
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // A full or disabled store costs a re-write later, and nothing else.
  }
}

export function cachedEdition(facts: PaperFacts): PaperArticle | null {
  return readCache()[editionKey(facts)] ?? null;
}

/** Every edition on file, so a reader can go back through the season. */
export function filedEditions(): PaperArticle[] {
  return Object.values(readCache());
}

/**
 * D-EZ-70 — THE HEADLINES THIS PAPER HAS ALREADY RUN, newest first.
 *
 * A model writing edition fourteen cannot see editions one to thirteen, so it
 * reaches for the same construction every week and the page starts to feel like
 * a template even when every fact in it is different. This is the cheapest
 * possible answer: show it what it just published.
 *
 * ⚠ THIS LEAGUE AND THIS SEASON ONLY. The cache is one store for every league a
 * browser has ever opened; feeding a RASCAL writer the headlines from somebody
 * else's league would have him avoiding constructions he never used.
 */
export function recentHeadlines(facts: PaperFacts, limit = 3): string[] {
  const mine = `${facts.league}|s${facts.season}|`;
  const key = editionKey(facts);
  return Object.entries(readCache())
    .filter(([k]) => k.startsWith(mine) && k !== key)
    .map(([, a]) => a.headline)
    .filter((h) => typeof h === 'string' && h.length > 0)
    .slice(-limit)
    .reverse();
}

export function forgetEditions(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to forget */
  }
}

// ---------------------------------------------------------------------------
// Is there a key anywhere
// ---------------------------------------------------------------------------

let availability: Promise<boolean> | null = null;
let availabilityFor = -1;

/**
 * Can the paper be written by a model at all? Cached per key, like narration's
 * probe and for the same reason — and invalidated the moment somebody locks a
 * key in, which is the whole reason `aiKey.ts` counts versions.
 */
export function paperAvailable(): Promise<boolean> {
  if (availability !== null && availabilityFor === aiSetupVersion()) return availability;
  availabilityFor = aiSetupVersion();
  availability = aiAccess()
    .then(async (access) => {
      // D-EZ-100 — nobody's key to spend: the game writes the paper, unasked.
      if (access.payer === 'none') return false;
      const r = await fetch('/api/paper', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...access.headers },
        body: JSON.stringify({ facts: { probe: true }, edition: 'race' }),
      });
      if (!r.ok) return false;
      const body = (await r.json().catch(() => null)) as { ok?: unknown } | null;
      return body?.ok === true && !isNoModel(body);
    })
    .catch(() => false);
  return availability;
}

/** Test seam. */
export function resetPaperAvailability(): void {
  availability = null;
  availabilityFor = -1;
}

// ---------------------------------------------------------------------------
// The one call a screen makes
// ---------------------------------------------------------------------------

export interface ReadOptions {
  /** Skip the cache and buy a fresh one. The modal's "write it again" button. */
  fresh?: boolean;
  /** Never call the model, whatever is configured. */
  offline?: boolean;
}

export interface PaperOutcome {
  article: PaperArticle;
  /** Why a player is looking at the written-here version, when they are. */
  reason: string | null;
  fromCache: boolean;
}

export async function readThePaper(facts: PaperFacts, options: ReadOptions = {}): Promise<PaperOutcome> {
  if (!options.fresh) {
    const filed = cachedEdition(facts);
    if (filed) return { article: filed, reason: null, fromCache: true };
  }

  const written = fallbackPaper(facts);
  if (options.offline) return { article: written, reason: 'written without a model, by request', fromCache: false };

  if (!(await paperAvailable())) {
    return {
      article: written,
      reason: 'written by the game itself',
      fromCache: false,
    };
  }
  const access = await aiAccess();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch('/api/paper', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...access.headers },
      signal: controller.signal,
      body: JSON.stringify({
        facts,
        edition: facts.edition,
        /*
          D-EZ-70 — the assignment, worked out HERE because this side holds the
          facts, and re-checked over there because a request body is a request
          body. See `angle.ts`.
        */
        angle: angleFor(facts),
        recent: recentHeadlines(facts),
        // The dateline is a FACT and belongs to the calendar, which this side
        // has and the Worker does not. The byline is the paper's and is chosen
        // over there, where a request body cannot name it.
        dateline: facts.race?.location ?? facts.race?.track ?? '',
        index: facts.racesRun + facts.season,
      }),
    });

    if (!res.ok) {
      const detail = await reasonFrom(res);
      return { article: written, reason: detail, fromCache: false };
    }

    const payload = (await res.json()) as Partial<PaperArticle>;
    if (isNoModel(payload)) return { article: written, reason: 'written by the game itself', fromCache: false };
    if (!payload || typeof payload.headline !== 'string' || !Array.isArray(payload.paragraphs)) {
      return { article: written, reason: 'the model sent something the paper could not print', fromCache: false };
    }

    const article: PaperArticle = {
      masthead: payload.masthead ?? written.masthead,
      motto: payload.motto ?? written.motto,
      edition: facts.edition,
      headline: payload.headline,
      standfirst: payload.standfirst ?? '',
      byline: payload.byline ?? written.byline,
      dateline: payload.dateline || written.dateline,
      paragraphs: payload.paragraphs as string[],
      source: 'ai',
      model: payload.model ?? null,
      paidBy: payload.paidBy ?? 'server',
      angle: payload.angle ?? written.angle,
    };

    const cache = readCache();
    cache[editionKey(facts)] = article;
    writeCache(cache);
    return { article, reason: null, fromCache: false };
  } catch (e) {
    const aborted = (e as Error)?.name === 'AbortError';
    return {
      article: written,
      reason: aborted ? 'the model took too long, so the game wrote it' : 'the model could not be reached',
      fromCache: false,
    };
  } finally {
    clearTimeout(timer);
  }
}

async function reasonFrom(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { reason?: unknown };
    if (typeof body.reason === 'string' && body.reason.length > 0) return body.reason;
  } catch {
    /* fall through */
  }
  return `the model refused (${res.status})`;
}
